import { Component, inject, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TenantService } from '../../../shared/services/tenant.service';
import { UserService } from '../../../shared/services/user.service';
import { User, Tenant, UserTenant } from '../../../shared/models/user.model';

@Component({
  selector: 'app-edit-employee-tenants',
  imports: [CommonModule, FormsModule],
  templateUrl: './edit-employee-tenants.html'
})
export class EditEmployeeTenantsComponent implements OnInit {
  private tenantService = inject(TenantService);
  private userService = inject(UserService);

  @Input() employee!: User;
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  allTenants: Tenant[] = [];
  assignedTenantIds: Set<string> = new Set();
  originalTenantIds: Set<string> = new Set();

  loading = true;
  saving = false;

  async ngOnInit() {
    this.allTenants = await this.tenantService.listTenants();

    // Initialize assigned tenants from employee data
    if (this.employee.tenants) {
      this.employee.tenants.forEach(ut => {
        if (ut.tenantId) {
          this.assignedTenantIds.add(ut.tenantId);
          this.originalTenantIds.add(ut.tenantId);
        }
      });
    }
    this.loading = false;
  }

  isTenantAssigned(tenantId: string): boolean {
    return this.assignedTenantIds.has(tenantId);
  }

  toggleTenant(tenantId: string) {
    if (this.assignedTenantIds.has(tenantId)) {
      this.assignedTenantIds.delete(tenantId);
    } else {
      this.assignedTenantIds.add(tenantId);
    }
  }

  hasChanges(): boolean {
    if (this.assignedTenantIds.size !== this.originalTenantIds.size) return true;
    for (const id of this.assignedTenantIds) {
      if (!this.originalTenantIds.has(id)) return true;
    }
    return false;
  }

  async save() {
    if (!this.employee.id || !this.hasChanges()) {
      this.close.emit();
      return;
    }

    this.saving = true;
    try {
      // Find tenants to add and remove
      const toAdd = [...this.assignedTenantIds].filter(id => !this.originalTenantIds.has(id));
      const toRemove = [...this.originalTenantIds].filter(id => !this.assignedTenantIds.has(id));

      // Add new tenant associations
      for (const tenantId of toAdd) {
        await this.userService.addUserToTenant(this.employee.id, tenantId);
      }

      // Remove tenant associations
      for (const tenantId of toRemove) {
        const userTenant = this.employee.tenants?.find(ut => ut.tenantId === tenantId);
        if (userTenant?.id) {
          await this.userService.removeUserFromTenant(userTenant.id);
        }
      }

      this.saved.emit();
      this.close.emit();
    } catch (error) {
      console.error('Error updating user tenants:', error);
      alert('Failed to update tenants. Please try again.');
    } finally {
      this.saving = false;
    }
  }

  closeModal() {
    this.close.emit();
  }
}
